import { ArrowLeft } from 'lucide-react';
import {
  Badge,
  Button,
  ChoiceChips,
  formatDateTime,
  formatMoney,
  formatNumber,
  KpiTile,
  PageHeader,
  remittanceOutcomeTone,
  type Column,
} from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { ResourceTable } from '../ResourceTable.tsx';
import { usePageContext } from '../usePageContext.ts';
import { linesInView, OUTCOME_VIEWS, useRemittance, type ImportedLine, type OutcomeView } from './finance.ts';
import styles from './Finance.module.css';
import { TrackingCode } from '../TrackingCode.tsx';

export type RemittancePageProps = { api: TypedClient; remittanceId: string; onBack: () => void };

export const RemittancePage = ({ api, remittanceId, onBack }: RemittancePageProps) => {
  const { t, locale } = usePageContext();
  const detail = useRemittance(api, remittanceId);
  const currency = useTenantDetail(api).data?.currency;
  const [view, setView] = useState<OutcomeView>('all');
  const remittance = detail.data?.remittance;
  const money = (amount: string | null) =>
    amount === null || currency === undefined ? '—' : formatMoney({ amount, currency }, locale);

  const columns: readonly Column<ImportedLine>[] = [
    { id: 'position', header: '#', numeric: true, cell: (line) => formatNumber(line.position + 1, locale) },
    {
      id: 'tracking',
      header: t('remittances.trackingNumber'),
      primary: true,
      cell: (line) => <TrackingCode code={line.trackingNumber} />,
    },
    { id: 'amount', header: t('remittances.amount'), numeric: true, cell: (line) => money(line.amount) },
    { id: 'fee', header: t('remittances.fee'), numeric: true, cell: (line) => money(line.fee) },
    {
      id: 'outcome',
      header: t('remittances.outcome'),
      cell: (line) => (
        <Badge tone={remittanceOutcomeTone[line.outcome]}>{t(`remittances.outcome_${line.outcome}`)}</Badge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          remittance === undefined
            ? t('remittances.detailTitle')
            : t('remittances.detailTitleOf', { date: formatDateTime(remittance.createdAt, locale) })
        }
        context={t('remittances.detailContext')}
        action={
          <Button
            icon={<ArrowLeft size={20} aria-hidden="true" className="flip-inline" />}
            variant="outline"
            onClick={onBack}
          >
            {t('remittances.back')}
          </Button>
        }
      />
      <div className={styles['kpis']}>
        {(['lineCount', 'settledCount', 'disputedCount', 'attentionCount'] as const).map((key) => (
          <KpiTile
            key={key}
            label={t(`remittances.kpi_${key}`)}
            value={remittance === undefined ? undefined : formatNumber(remittance[key], locale)}
            loading={detail.isPending}
          />
        ))}
      </div>
      <div className={styles['toolbar']}>
        <ChoiceChips
          legend={t('remittances.view')}
          hideLegend
          locale={locale}
          value={view}
          choices={OUTCOME_VIEWS.map((value) => ({ value, label: t(`remittances.view_${value}`) }))}
          onChange={setView}
        />
      </div>
      <ResourceTable
        query={detail}
        columns={columns}
        rows={linesInView(detail.data?.lines ?? [], view)}
        rowKey={(line) => String(line.position)}
        emptyTitle={t('remittances.noLines')}
      />
    </>
  );
};
